(ns dre.components.widget.views.table-list
  {:authors ["Miroslav Smatana"]}
  (:require
   [applied-science.js-interop :as j]
   [clj-commons.humanize :refer [intcomma]]
   [dre.components.tooltip :refer [tooltip-wrapper]]
   [dre.utils :refer [counts->percentages]]
   [dre.page-util :refer [round nbsp enum short-num ceil]]
   [dre.i18n.events :as-alias i18n]
   [goog.object]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [dre.components.icons-iconoir :refer [icoinor-icon]]
   [dre.components.icon :refer [fa-icon]]
   ["react-sizeme" :as react-sizeme]))

(def component-name :widget-table-list)

(defn scroll-wrap
  [{:keys [id table columns width height]} child]
  (let [pad 13.33
        state (r/atom {})
        get-parent #(j/call js/document :getElementById id)
        scroll?!
        (fn [table]
          (r/after-render
           (fn []
             (when-let [parent (get-parent)]
               (when-let [item (j/call parent :querySelector ".row-wrap:first-child")]
                 (let [item-height (ceil (+ (/ pad 2) (j/get item :clientHeight)))
                       item-width (ceil (+ pad (j/get item :clientWidth)))
                       rows (ceil (/ height item-height))
                       cols (ceil (/ width item-width))
                       scroll? (> (count table) (*  rows cols))]
                   (j/assoc! parent :scrollLeft 0)
                   (swap! state assoc
                          :left? false
                          :right? scroll?)))))))
        scroll
        (fn [op]
          (let [parent (get-parent)
                item (j/call parent :querySelector ".row-wrap:first-child")
                item-width (ceil (j/get item :clientWidth))
                curr (j/get parent :scrollLeft)
                next (op curr item-width pad)]
            (j/assoc! parent :scrollLeft next)
            (reset! state
                    {:left? (> next 0)
                     :right? (<= (+ 1 next)
                                 (- (j/get parent :scrollWidth)
                                    (j/get parent :offsetWidth)))})))]

    (r/create-class
     {:component-did-mount
      (fn [this] (scroll?! (:table (r/props this))))
      :component-did-update
      (fn [this [_ old-args _]]
        (let [new-args (r/props this)
              ks [:columns]
              old-args* (select-keys old-args ks)
              new-args* (select-keys new-args ks)
              update? (not= old-args* new-args*)]
          (when update?
            (scroll?! table))))
      :reagent-render
      (fn [{:keys [id table columns width height]} child]
        [:<>
         [:div.scroll-wrap-left
          {:on-click (fn [_e] (scroll -))
           :class [(when (:left? @state) "possible")]}
          (icoinor-icon :nav-arrow-down)]
         [:div.scroll-wrap-right
          {:on-click (fn [_e] (scroll +))
           :class [(when (:right? @state) "possible")]}
          (icoinor-icon :nav-arrow-down)]
         [:div.scroll-wrap-content
          child]])})))

(defn touch-scroll-wrap
  [_opts child]
  [:div {:style {:overflow :auto}}
   child])

(defn- assoc-percentages
  "Calculates percentage for each entry in the collection by count or score."
  ([rows score-fn]
   (assoc-percentages rows score-fn nil))
  ([rows score-fn total-count]
   (let [counts->percentages' (if (some? total-count)
                                #(counts->percentages % total-count)
                                counts->percentages)
         percentages (->> rows
                          (map :count)
                          counts->percentages')
         scores ((or score-fn
                     counts->percentages)
                 (map :score rows))]
     (map (fn [e p s]
            (assoc e :percentage
                   (first
                    (filter
                     (complement js/isNaN)
                     [p s]))))
          rows percentages scores))))

(defn- child-row
  [{:keys [style]} ; parent row
   {:keys [asis class count display-label label open-fn on-click percentage tooltip] :as row} ; child row
   without-percentage?
   comma-separate?
   title-fn]
  [tooltip-wrapper (when-not asis
                     (if title-fn
                       (title-fn row)
                       (str (or tooltip display-label label)
                            (when count
                              (str " - "
                                   (if comma-separate?
                                     (intcomma count)
                                     count)
                                   " results"))
                            (when-not without-percentage?
                              (str " ("  (round percentage) "%)")))))
   [:div.table-list-row
    (hash-map
     :class class
     :style {:margin-left "1.5em"})
    (when count
      [:div.row-counts (short-num count)])
    [:div.label-container
     (hash-map
      :style (assoc style :width (cond
                                   count "calc(100% - 44px)"
                                   :else "100%")))
     (when-not (or asis without-percentage?)
       [:div.row-percentage {:style {:width percentage}}])
     [:div.row-label
      (hash-map
       :style (assoc style :width (cond
                                    count "calc(100% - 28px)"
                                    :else "calc(100% - 28px )"))
       :on-click on-click)
      label
      [:span {:class :active-icon}
       [fa-icon :check]]
      (when open-fn
        [tooltip-wrapper (str "Open"
                              " "
                              "details")
         [:span.open
          {:on-click (fn [e]
                       (open-fn)
                       (.preventDefault e)
                       (.stopPropagation e))}
          [fa-icon :eye]]])]]]])

(defn- sort-rows
  "Sorts rows by sort-fn(if provided) or decreasing percentage."
  [rows sort-fn]
  (if sort-fn
    (sort-by sort-fn rows)
    (sort-by :percentage #(compare %2 %1) rows)))

(defn table-list-base
  [_id _width _height _table & _]
  (let [expanded (r/atom {})
        toggle-expanded! (fn [term] (swap! expanded update term not))]
    (fn [id width height table & {:keys [comma-separate? sort? sort-fn sort-children?
                                         sort-children-fn score-fn without-scroll-wrap?
                                         without-percentage? title-fn total-count]
                                  :or {sort? true}}]
      (let [table (assoc-percentages table score-fn total-count)
            table (if sort?
                    (sort-rows table sort-fn)
                    table)
            contains-children? (some :children table)
            table (if contains-children?
                    (map #(update % :children assoc-percentages score-fn total-count) table)
                    table)
            table (if (and contains-children? sort-children?)
                    (map #(update % :children sort-rows sort-children-fn) table)
                    table)
            ;; Columns number ceremony:
            rows (/ height 29)  ;; Naively assume height of row is 29px
            columns (min (js/Math.ceil (/ (count table) rows))
                         (js/Math.ceil (/ width 220)))] ;; Naively assume width of row is 220px
        [:div.table-list-container
         {:style {:height "100%"}}
         (when (seq table)
           [:div.table-list
            {:id id
             :style
             {:column-count columns
              :height (if (> columns 1)
                        "calc(100% - 13px)"
                        "calc(100% - 23px)")}}
            [(if without-scroll-wrap?
               touch-scroll-wrap scroll-wrap)             
             {:id id
              :table table
              :columns columns
              :width width
              :height height}
             [:<>
              (doall
               (for [[idx row] (enum table)]
                 (let [{:keys [percentage label display-label
                               style term count class children
                               open-fn on-click tooltip]} row
                       percent (str (round percentage) "%")
                       expanded? (get @expanded term)
                       has-children? (seq children)]
                   (when-let [label (or display-label label)]
                     (with-meta
                       [:div
                        [:div.row-wrap
                         {:class [(when (:asis row) "asis")]}
                         (when contains-children?
                           [:div.expand-controls
                            (if has-children?
                              [:span
                               {:style {:cursor :pointer}
                                :on-click #(toggle-expanded! term)}
                               (if expanded?
                                 [fa-icon :chevron-down]
                                 [fa-icon :chevron-right])]
                              [:span nbsp])])
                         [tooltip-wrapper (when-not (:asis row)
                                            (if title-fn
                                              (title-fn row)
                                              (str (or tooltip display-label label)
                                                   (when count
                                                     (str " - "
                                                          (if comma-separate?
                                                            (intcomma count)
                                                            count)
                                                          " results"))
                                                   (when (and
                                                          (some? count)
                                                          (not without-percentage?))
                                                     (str " ("  percent ")")))))
                          [:div.table-list-row
                           (hash-map
                            :class class
                            :on-click on-click
                            :style style)
                           (when count
                             [:div.row-counts (short-num count)])
                           [:div.label-container
                            {:style {:width
                                     (cond
                                       count "calc(100% - 44px)"
                                       :else "100%")}}
                            (when-not (or (:asis row) without-percentage?)
                              [:div.row-percentage {:style {:width percent}}])
                            [:div.row-label
                             {:style {:width
                                      (cond
                                        count "calc(100% - 28px)"
                                        :else "calc(100% - 28px)")}}
                             (if (or (:asis row) (:count row) (:percentage row))
                               [:span.truncate-label label]
                               label)
                             [:span {:class :active-icon}
                              [fa-icon :check]]
                             (when open-fn
                               [tooltip-wrapper (str "Open"
                                                     " "
                                                     "details")
                                [:span.open
                                 {:on-click (fn [e]
                                              (open-fn)
                                              (.preventDefault e)
                                              (.stopPropagation e))}
                                 [fa-icon :eye]]])]]]]]
                        (when expanded?
                          [:<>
                           (for [[c-idx child] (enum children)]
                             (with-meta
                               [child-row row child without-percentage? comma-separate? title-fn]
                               {:key (str idx "-" c-idx)}))])]
                       {:key idx})))))]]])]))))


(defn table-list
  [id data opts]
  [:div {:class component-name}
   [(r/adapt-react-class react-sizeme/SizeMe)
    {:monitorWidth true :monitorHeight true}
    (fn [props]
      (let [props (js->clj props :keywordize-keys true)]
        (r/as-element [table-list-base
                       id
                       (:width (:size props))
                       (:height (:size props))
                       data
                       opts])))]])